<script setup>
import {
  ref,
  computed,
  reactive,
  watch,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TicketsAPI from 'dashboard/api/tickets';
import {
  buildDateRangeParams,
  formatCustomRangeLabel,
} from 'dashboard/helper/ticketDateFilter';
import TicketRow from './TicketRow.vue';
import TicketDetails from './TicketDetails.vue';
import CreateTicketModal from './CreateTicketModal.vue';

const store = useStore();
const { t } = useI18n();

const PER_PAGE = 100;

const viewMode = ref('list'); // 'list' | 'board'
const groupBy = ref('status'); // status | priority | assignee | category | team
const sortBy = ref('created'); // created | sla | priority | title
const query = ref('');
const activeFilter = ref('all');
const dateRange = ref('all'); // all | today | last_7_days | last_30_days | this_month | custom
const customFrom = ref(''); // YYYY-MM-DD from <input type="date">
const customTo = ref('');
const dateMenuOpen = ref(false); // date filter dropdown (presets + custom range, one panel)
const collapsed = reactive({});

const records = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCountAll = ref(0);
const showCreateModal = ref(false);

// Ticket detail opens in a modal over the list (no page navigation).
const selectedTicketId = ref(null);

const columns = ref([]);

const currentUserId = computed(() => store.getters.getCurrentUser?.id);

const STATUS_ORDER = [
  'open',
  'in_progress',
  'escalated',
  'waiting_client',
  'waiting_internal',
  'resolved',
  'closed',
  'cancelled',
];
const STATUS_DOT = {
  open: 'bg-n-slate-9',
  in_progress: 'bg-n-blue-9',
  escalated: 'bg-n-ruby-9',
  waiting_client: 'bg-n-amber-9',
  waiting_internal: 'bg-n-amber-9',
  resolved: 'bg-n-teal-9',
  closed: 'bg-n-slate-8',
  cancelled: 'bg-n-slate-8',
};
const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];
const PRIORITY_DOT = {
  urgent: 'bg-n-ruby-9',
  high: 'bg-n-amber-9',
  medium: 'bg-n-blue-9',
  low: 'bg-n-slate-9',
};
const PRIORITY_RANK = { urgent: 4, high: 3, medium: 2, low: 1 };

const statusLabel = s => t(`TICKETS.STATUS.${s.toUpperCase()}`);
const priorityLabel = p => t(`TICKETS.PRIORITY.${p.toUpperCase()}`);

const QUICK_FILTERS = ['all', 'mine', 'unassigned', 'overdue', 'escalated'];
const quickFilterLabel = key =>
  ({
    all: t('TICKETS.FILTER_TABS.ALL'),
    mine: t('TICKETS.FILTER_TABS.MINE'),
    unassigned: t('TICKETS.FILTER_TABS.UNASSIGNED'),
    overdue: t('TICKETS.FILTER_TABS.OVERDUE'),
    escalated: t('TICKETS.FILTER_TABS.ESCALATED'),
  })[key];

const isActiveStatus = ticket =>
  !['resolved', 'closed', 'cancelled'].includes(ticket.status);
const isOverdue = ticket => {
  if (ticket.sla_breached) return true;
  if (!ticket.sla_deadline || !isActiveStatus(ticket)) return false;
  return Date.parse(ticket.sla_deadline) < Date.now();
};

const matchesQuickFilter = ticket => {
  switch (activeFilter.value) {
    case 'mine':
      return ticket.assignee_id === currentUserId.value;
    case 'unassigned':
      return !ticket.assignee_id;
    case 'overdue':
      return isOverdue(ticket);
    case 'escalated':
      return ticket.status === 'escalated';
    default:
      return true;
  }
};

const sortTickets = list => {
  const arr = [...list];
  switch (sortBy.value) {
    case 'sla':
      return arr.sort(
        (a, b) =>
          (a.sla_deadline ? Date.parse(a.sla_deadline) : Infinity) -
          (b.sla_deadline ? Date.parse(b.sla_deadline) : Infinity)
      );
    case 'priority':
      return arr.sort(
        (a, b) => PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority]
      );
    case 'title':
      return arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    default:
      return arr.sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)
      );
  }
};

const columnDefs = base => {
  if (groupBy.value === 'priority') {
    return PRIORITY_ORDER.map(key => ({
      key,
      label: priorityLabel(key),
      dot: PRIORITY_DOT[key],
    }));
  }
  if (groupBy.value === 'assignee') {
    const seen = new Map();
    base.forEach(ticket => {
      const key = ticket.assignee_id
        ? String(ticket.assignee_id)
        : 'unassigned';
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          label: ticket.assignee?.name || t('TICKETS.UNASSIGNED'),
          dot: 'bg-n-slate-9',
        });
      }
    });
    return [...seen.values()];
  }
  if (groupBy.value === 'category') {
    const seen = new Map();
    base.forEach(ticket => {
      const key = ticket.ticket_category_id
        ? String(ticket.ticket_category_id)
        : 'uncategorized';
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          label:
            ticket.ticket_category?.name || t('TICKETS.FILTERS.ALL_CATEGORIES'),
          dot: 'bg-n-slate-9',
          locked: key === 'uncategorized', // category is required — disallow drops here
        });
      }
    });
    return [...seen.values()];
  }
  if (groupBy.value === 'team') {
    const seen = new Map();
    base.forEach(ticket => {
      const key = ticket.team_id ? String(ticket.team_id) : 'no_team';
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          label: ticket.team?.name || t('TICKETS.FILTERS.NO_TEAM'),
          dot: 'bg-n-slate-9',
        });
      }
    });
    return [...seen.values()];
  }
  return STATUS_ORDER.map(key => ({
    key,
    label: statusLabel(key),
    dot: STATUS_DOT[key],
  }));
};

const inColumn = (ticket, key) => {
  if (groupBy.value === 'priority') return ticket.priority === key;
  if (groupBy.value === 'assignee') {
    return key === 'unassigned'
      ? !ticket.assignee_id
      : String(ticket.assignee_id) === key;
  }
  if (groupBy.value === 'category') {
    return key === 'uncategorized'
      ? !ticket.ticket_category_id
      : String(ticket.ticket_category_id) === key;
  }
  if (groupBy.value === 'team') {
    return key === 'no_team' ? !ticket.team_id : String(ticket.team_id) === key;
  }
  return ticket.status === key;
};

const buildColumns = () => {
  const q = query.value.trim().toLowerCase();
  const base = sortTickets(
    records.value.filter(matchesQuickFilter).filter(ticket => {
      if (!q) return true;
      return (
        (ticket.title || '').toLowerCase().includes(q) ||
        (ticket.folio || '').toLowerCase().includes(q) ||
        (ticket.client_name || '').toLowerCase().includes(q)
      );
    })
  );
  columns.value = columnDefs(base).map(def => ({
    ...def,
    items: base.filter(ticket => inColumn(ticket, def.key)),
  }));
};

watch([records, groupBy, sortBy, query, activeFilter], buildColumns, {
  deep: true,
  immediate: true,
});

// No `immediate` — onMounted owns the initial fetch. Debounced because the
// custom date inputs fire the watcher on every keystroke.
let dateFilterTimer = null;
watch([dateRange, customFrom, customTo], () => {
  clearTimeout(dateFilterTimer);
  dateFilterTimer = setTimeout(() => {
    currentPage.value = 1;
    fetchTickets();
  }, 350);
});

const closeDateMenu = () => {
  dateMenuOpen.value = false;
};
// Presets apply and close the panel; "custom" keeps it open so the user can
// fill in the date inputs that appear inside the same panel.
const selectDatePreset = value => {
  dateRange.value = value;
  if (value !== 'custom') dateMenuOpen.value = false;
};

const totalCount = computed(() =>
  columns.value.reduce((sum, c) => sum + c.items.length, 0)
);
const hasMore = computed(() => currentPage.value < totalPages.value);

const groupByOptions = computed(() => [
  { value: 'status', label: t('TICKETS.SORT.STATUS') },
  { value: 'priority', label: t('TICKETS.SORT.PRIORITY') },
  { value: 'assignee', label: t('TICKETS.FILTERS.BY_AGENT') },
  { value: 'category', label: t('TICKETS.FILTERS.CATEGORY') },
  { value: 'team', label: t('TICKETS.FILTERS.BY_TEAM') },
]);
const sortOptions = computed(() => [
  { value: 'created', label: t('TICKETS.SORT.NEWEST') },
  { value: 'sla', label: t('TICKETS.SORT_BY_OPTIONS.SLA') },
  { value: 'priority', label: t('TICKETS.SORT.PRIORITY') },
  { value: 'title', label: t('TICKETS.SORT_BY_OPTIONS.TITLE') },
]);
const groupByLabel = computed(
  () => groupByOptions.value.find(o => o.value === groupBy.value)?.label
);
const sortLabel = computed(
  () => sortOptions.value.find(o => o.value === sortBy.value)?.label
);

const dateFilterOptions = computed(() => [
  { value: 'all', label: t('TICKETS.DATE_FILTER.ALL_TIME') },
  { value: 'today', label: t('TICKETS.DATE_FILTER.TODAY') },
  { value: 'last_7_days', label: t('TICKETS.DATE_FILTER.LAST_7_DAYS') },
  { value: 'last_30_days', label: t('TICKETS.DATE_FILTER.LAST_30_DAYS') },
  { value: 'this_month', label: t('TICKETS.DATE_FILTER.THIS_MONTH') },
  { value: 'custom', label: t('TICKETS.DATE_FILTER.CUSTOM') },
]);
const dateFilterLabel = computed(() => {
  if (dateRange.value === 'custom') {
    const label = formatCustomRangeLabel(customFrom.value, customTo.value);
    if (label) return label;
  }
  return dateFilterOptions.value.find(o => o.value === dateRange.value)?.label;
});

const fetchTickets = async (append = false) => {
  isLoading.value = true;
  try {
    const response = await TicketsAPI.get({
      page: currentPage.value,
      per_page: PER_PAGE,
      ...buildDateRangeParams(dateRange.value, {
        from: customFrom.value,
        to: customTo.value,
      }),
    });
    const data = response.data || {};
    const payload = data.payload || data.data || [];
    records.value = append ? [...records.value, ...payload] : payload;
    const meta = data.meta || {};
    totalPages.value = meta.total_pages || 1;
    totalCountAll.value =
      meta.total_count || meta.count || records.value.length;
  } finally {
    isLoading.value = false;
  }
};

const loadMore = () => {
  if (!hasMore.value || isLoading.value) return;
  currentPage.value += 1;
  fetchTickets(true);
};

const isTicketModalOpen = computed(() => Boolean(selectedTicketId.value));

const openTicket = ticket => {
  selectedTicketId.value = ticket.display_id;
};

const closeTicketModal = () => {
  selectedTicketId.value = null;
  fetchTickets(); // reconcile any edits made in the modal
};

const onModalNavigate = displayId => {
  selectedTicketId.value = displayId;
};

const onModalKeydown = e => {
  if (e.key === 'Escape' && isTicketModalOpen.value) closeTicketModal();
};

const onTicketCreated = () => {
  showCreateModal.value = false;
  currentPage.value = 1;
  fetchTickets();
};

const toggleCollapse = key => {
  collapsed[key] = !collapsed[key];
};

// Apply the grouped attribute when a ticket is dragged into a column.
// Terminal status transitions go through the lifecycle endpoints so SLA /
// verification side-effects are not bypassed.
const applyStatusChange = async (ticket, key) => {
  if (ticket.status === key) return;
  if (key === 'resolved') {
    await TicketsAPI.resolve(ticket.display_id);
  } else if (key === 'closed') {
    await TicketsAPI.close(ticket.display_id);
  } else if (
    ['resolved', 'closed'].includes(ticket.status) &&
    ['open', 'in_progress'].includes(key)
  ) {
    await TicketsAPI.reopen(ticket.display_id);
  } else {
    await TicketsAPI.update(ticket.display_id, { status: key });
  }
  ticket.status = key;
};

const applyGroupChange = async (ticket, column) => {
  if (!ticket || column.locked) return;
  const { key } = column;
  try {
    if (groupBy.value === 'priority') {
      if (ticket.priority !== key) {
        await TicketsAPI.update(ticket.display_id, { priority: key });
        ticket.priority = key;
      }
    } else if (groupBy.value === 'assignee') {
      const assigneeId = key === 'unassigned' ? null : Number(key);
      if (assigneeId !== (ticket.assignee_id ?? null)) {
        await TicketsAPI.assign(ticket.display_id, assigneeId);
        ticket.assignee_id = assigneeId;
      }
    } else if (groupBy.value === 'category') {
      if (String(ticket.ticket_category_id) !== key) {
        await TicketsAPI.update(ticket.display_id, {
          ticket_category_id: Number(key),
        });
        ticket.ticket_category_id = Number(key);
      }
    } else if (groupBy.value === 'team') {
      const teamId = key === 'no_team' ? null : Number(key);
      if (teamId !== (ticket.team_id ?? null)) {
        await TicketsAPI.update(ticket.display_id, { team_id: teamId });
        ticket.team_id = teamId;
      }
    } else {
      await applyStatusChange(ticket, key);
    }
  } catch {
    // Re-sync from the server if the move was rejected.
    fetchTickets();
  }
};

const onColumnChange = (column, evt) => {
  if (evt.added) applyGroupChange(evt.added.element, column);
};

onMounted(() => {
  fetchTickets();
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  document.addEventListener('keydown', onModalKeydown);
});

onBeforeUnmount(() => {
  clearTimeout(dateFilterTimer);
  document.removeEventListener('keydown', onModalKeydown);
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex flex-col gap-3 px-6 pt-5 pb-3 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center justify-between gap-3">
        <h1
          class="flex items-center gap-2 text-xl font-semibold text-n-slate-12"
        >
          {{ t('TICKETS.HEADER_TITLE') }}
          <span class="text-base font-normal text-n-slate-10">
            {{ totalCountAll }}
          </span>
        </h1>
        <div class="flex items-center gap-2">
          <!-- View toggle -->
          <div class="flex p-0.5 rounded-lg bg-n-alpha-1">
            <button
              v-for="mode in ['list', 'board']"
              :key="mode"
              type="button"
              class="flex items-center gap-1.5 px-2.5 h-7 text-sm rounded-md transition-colors"
              :class="
                viewMode === mode
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="viewMode = mode"
            >
              <span
                :class="
                  mode === 'list' ? 'i-lucide-list' : 'i-lucide-columns-3'
                "
              />
              {{ t(`TICKETS.VIEWS.${mode.toUpperCase()}`) }}
            </button>
          </div>
          <div class="relative w-52 max-w-full">
            <span
              class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10"
            />
            <input
              v-model="query"
              type="text"
              :placeholder="t('TICKETS.SEARCH_PLACEHOLDER')"
              class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            />
          </div>
          <Button
            :label="t('TICKETS.CREATE_TICKET')"
            icon="i-lucide-plus"
            size="sm"
            color="blue"
            @click="showCreateModal = true"
          />
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <!-- Quick filters -->
        <nav class="flex items-center gap-1.5">
          <button
            v-for="key in QUICK_FILTERS"
            :key="key"
            class="px-2.5 h-7 text-sm rounded-lg transition-colors"
            :class="
              activeFilter === key
                ? 'bg-n-brand/10 text-n-blue-text font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="activeFilter = key"
          >
            {{ quickFilterLabel(key) }}
          </button>
        </nav>

        <!-- Group by + sort -->
        <div class="flex items-center gap-2">
          <!-- Date range filter — one self-contained panel holds both the
               presets and the custom date inputs, so the toolbar never wraps
               and there is no second floating layer to overlap or mis-close. -->
          <div
            v-on-clickaway="closeDateMenu"
            class="relative flex items-center gap-1.5"
          >
            <span class="i-lucide-calendar text-n-slate-10" />
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              trailing-icon
              color="slate"
              variant="faded"
              class="!w-fit max-w-48"
              :label="dateFilterLabel"
              @click="dateMenuOpen = !dateMenuOpen"
            />
            <div
              v-if="dateMenuOpen"
              class="absolute z-40 flex flex-col w-56 gap-1 p-1 border rounded-lg shadow-lg select-none top-full mt-1 ltr:left-0 rtl:right-0 bg-n-alpha-3 backdrop-blur-[100px] border-n-weak dark:border-n-strong/50"
            >
              <Button
                v-for="option in dateFilterOptions"
                :key="option.value"
                :label="option.label"
                :icon="dateRange === option.value ? 'i-lucide-check' : ''"
                size="sm"
                variant="ghost"
                color="slate"
                trailing-icon
                class="!justify-start !w-full !px-2.5 !h-7"
                :class="{ '!bg-n-alpha-2': dateRange === option.value }"
                @click="selectDatePreset(option.value)"
              />
              <div
                v-if="dateRange === 'custom'"
                class="flex flex-col gap-2 p-2 mt-1 border-t border-n-weak"
              >
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('TICKETS.DATE_FILTER.FROM') }}
                  <input
                    v-model="customFrom"
                    type="date"
                    :max="customTo || undefined"
                    class="h-8 px-2 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
                  />
                </label>
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('TICKETS.DATE_FILTER.TO') }}
                  <input
                    v-model="customTo"
                    type="date"
                    :min="customFrom || undefined"
                    class="h-8 px-2 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand"
                  />
                </label>
              </div>
            </div>
          </div>
          <span class="text-xs text-n-slate-10">{{
            t('TICKETS.GROUP_BY')
          }}</span>
          <SelectMenu
            v-model="groupBy"
            :options="groupByOptions"
            :label="groupByLabel"
            sub-menu-position="bottom"
          />
          <span class="text-xs text-n-slate-10">{{
            t('TICKETS.SORT.ORDER_BY')
          }}</span>
          <SelectMenu
            v-model="sortBy"
            :options="sortOptions"
            :label="sortLabel"
            sub-menu-position="bottom"
          />
        </div>
      </div>
    </header>

    <p
      v-if="!totalCount && !isLoading"
      class="p-12 text-sm italic text-center text-n-slate-10"
    >
      {{
        records.length
          ? t('TICKETS.FILTERS.NO_RESULTS')
          : t('TICKETS.EMPTY_STATE')
      }}
    </p>

    <!-- ===================== LIST VIEW ===================== -->
    <div v-else-if="viewMode === 'list'" class="flex-1 overflow-y-auto">
      <section
        v-for="column in columns"
        :key="column.key"
        class="border-b border-n-weak last:border-b-0"
      >
        <button
          type="button"
          class="sticky top-0 z-10 flex items-center w-full gap-2 px-6 py-2 text-left border-b border-n-weak bg-n-background/95 backdrop-blur"
          @click="toggleCollapse(column.key)"
        >
          <span
            class="i-lucide-chevron-down text-n-slate-10 transition-transform"
            :class="{ '-rotate-90': collapsed[column.key] }"
          />
          <span class="w-2 h-2 rounded-full" :class="column.dot" />
          <span class="text-sm font-semibold uppercase text-n-slate-12">
            {{ column.label }}
          </span>
          <span class="text-sm text-n-slate-10">{{ column.items.length }}</span>
        </button>

        <div v-show="!collapsed[column.key]">
          <Draggable
            :list="column.items"
            :group="{ name: 'tickets' }"
            item-key="id"
            :animation="150"
            ghost-class="opacity-40"
            class="divide-y divide-n-weak/60"
            @change="onColumnChange(column, $event)"
          >
            <template #item="{ element }">
              <div>
                <TicketRow :ticket="element" layout="row" @open="openTicket" />
              </div>
            </template>
          </Draggable>
        </div>
      </section>

      <div v-if="hasMore" class="flex justify-center py-4">
        <Button
          :label="t('TICKETS.PAGINATION.LOAD_MORE')"
          variant="faded"
          color="slate"
          size="sm"
          :is-loading="isLoading"
          @click="loadMore"
        />
      </div>
    </div>

    <!-- ===================== BOARD VIEW ===================== -->
    <div v-else class="flex flex-1 gap-4 px-6 py-4 overflow-x-auto">
      <section
        v-for="column in columns"
        :key="column.key"
        class="flex flex-col w-72 max-h-full shrink-0 rounded-xl bg-n-alpha-1"
      >
        <div class="flex items-center gap-2 px-3 py-2.5 shrink-0">
          <span class="w-2 h-2 rounded-full" :class="column.dot" />
          <span class="text-sm font-semibold uppercase text-n-slate-12">
            {{ column.label }}
          </span>
          <span class="text-sm text-n-slate-10">{{ column.items.length }}</span>
        </div>

        <Draggable
          :list="column.items"
          :group="{ name: 'tickets' }"
          item-key="id"
          :animation="150"
          ghost-class="opacity-40"
          class="flex flex-col flex-1 gap-2 px-2 pb-2 overflow-y-auto min-h-[40px]"
          @change="onColumnChange(column, $event)"
        >
          <template #item="{ element }">
            <TicketRow :ticket="element" layout="card" @open="openTicket" />
          </template>
        </Draggable>
      </section>
    </div>

    <!-- Create ticket modal (reuses existing flow — tickets need category/contract) -->
    <CreateTicketModal
      v-if="showCreateModal"
      :show="showCreateModal"
      @close="showCreateModal = false"
      @created="onTicketCreated"
    />

    <!-- Ticket detail modal (opens over the list, like Tasks) -->
    <Teleport to="body">
      <div
        v-if="isTicketModalOpen"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-n-alpha-black1 backdrop-blur-sm"
        @click.self="closeTicketModal"
      >
        <div
          class="relative flex flex-col w-full overflow-hidden shadow-2xl max-w-7xl h-[90vh] rounded-2xl bg-n-background outline outline-1 outline-n-container"
        >
          <button
            type="button"
            class="absolute z-20 flex items-center justify-center w-8 h-8 rounded-lg top-3 right-3 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="closeTicketModal"
          >
            <span class="i-lucide-x" />
          </button>
          <TicketDetails
            :key="selectedTicketId"
            :ticket-id="selectedTicketId"
            embedded
            @navigate="onModalNavigate"
            @close="closeTicketModal"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>
